import React from "react";
import { Box, Link, Typography } from "@mui/material";
import { getNinjaOneRedirectUri } from "../../utils/ninjaOneOAuth";

export interface NinjaOneRmmGuideInnerProps {
  showGuideTitle?: boolean;
}

/**
 * NinjaOne RMM setup guide shared by integration setup surfaces.
 * Grep: NinjaOne setup guide, OAuth refresh token, Authorization Code
 */
export const NinjaOneRmmGuideInner: React.FC<NinjaOneRmmGuideInnerProps> = ({
  showGuideTitle = true,
}) => {
  const callbackUrl = getNinjaOneRedirectUri();

  return (
    <>
      {showGuideTitle ? (
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: 15,
            color: "#1a1a1a",
            mb: 1.5,
          }}
        >
          Setup Guide — NinjaOne
        </Typography>
      ) : null}

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        Instance URL
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5, mb: 2 }}>
        Use the NinjaOne URL you sign in to, without a trailing slash. For example:{" "}
        <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
          https://us2.ninjarmm.com
        </Typography>
        .
      </Typography>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mb: 1 }}>
        Client App
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, mb: 2, "& li": { mb: 0.75 } }}>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In NinjaOne, go to <strong>Administration</strong> → <strong>Apps</strong> → <strong>API</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Open <strong>Client app IDs</strong>, then click <strong>Add client app</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In <strong>Application platform</strong>, choose <strong>Web</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Set <strong>Name</strong> to something recognizable, such as Theo AI.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In <strong>Redirect URIs</strong>, add{" "}
          <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 12 }}>
            {callbackUrl}
          </Typography>
          .
        </Typography>
        <Box component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In <strong>Scopes</strong>, check:
          <Box component="ol" sx={{ mt: 0.75, pl: 0, listStyle: "none" }}>
            <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
              6.1 <strong>Monitoring</strong>: read-only device and organization data.
            </Typography>
            <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
              6.2 <strong>Management</strong>: NinjaOne script runs and Theo secure shell bootstrap scripts.
            </Typography>
          </Box>
        </Box>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          In <strong>Allowed grant types</strong>, check <strong>Authorization code</strong> and <strong>Refresh token</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Save the app and copy its <strong>Client ID</strong> and <strong>Client Secret</strong>.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Click <strong>Get Refresh Token</strong> in Theo and sign in with a technician account that can run
          automations.
        </Typography>
        <Typography component="li" sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
          Click <strong>Test</strong>, then <strong>Save</strong> after Theo confirms the connection.
        </Typography>
      </Box>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#666", lineHeight: 1.5 }}>
        NinjaOne documents this setup in its{" "}
        <Link
          href="https://www.ninjaone.com/docs/integrations/how-to-set-up-api-oauth-token/"
          target="_blank"
          rel="noreferrer"
          sx={{ color: "#1976d2", fontFamily: "var(--theo-font-sans)" }}
        >
          API token setup guide
        </Link>
        .
      </Typography>

      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#1a1a1a", mt: 2, mb: 1 }}>
        Secure Shell Script
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#555", lineHeight: 1.5 }}>
        After saving the RMM integration, open the NinjaOne configuration page in Theo and choose the NinjaOne automation
        scripts Theo should run to start relay-backed Windows and macOS secure shell sessions. Keep regular scripts selected separately.
      </Typography>
    </>
  );
};
